import { useEffect, useRef, useState } from 'react';
import {
  createContact,
  updateContact,
  extrairePiecesDemande,
} from '../services/contactService';
import { getClients } from '../services/clientService';
import { deposerDocumentContact } from '../services/documentClientService';
import SelectRecherche from '../components/SelectRecherche';
import { CATEGORIES_DOCUMENT, FORMATS_ACCEPTES } from './categoriesDocument';
import { STATUT_GENERANT } from './statutsDemande';
import BarreProgression, { useProgression } from '../components/BarreProgression';
import RemplirParTexte from '../components/RemplirParTexte';
import '../assets/styles/DevisPanel.css';

/**
 * Saisie d'une demande de devis, et correction d'une demande existante.
 *
 * En production les demandes arrivent par l'import Trello ; ce formulaire
 * sert à en créer sans passer par Trello, notamment pour éprouver
 * l'extraction IA — c'est le champ « Message » qu'elle analyse.
 *
 * À la création, plus de champs de coordonnées : on colle la demande telle
 * quelle (fiche Trello, e-mail du client) dans le message, et le serveur y
 * relit nom, e-mail, société, effectif… (« Nom : », « Email : »…) — le même
 * lecteur que l'import Trello ; ce qu'il n'y trouve pas, l'IA le pose sur la
 * demande à la génération du devis. Les champs ne restent affichés qu'en
 * correction, où l'on retouche justement une coordonnée.
 *
 * Avec `demande`, il corrige au lieu de créer. Le serveur refuse la
 * correction dès qu'un devis existe : le devis fige la demande telle qu'elle
 * a été comprise. La liste ne propose donc « Modifier » que sur une demande
 * sans devis.
 */

/**
 * Exemple montré en filigrane du message : une demande telle qu'elle arrive,
 * coordonnées en tête à la façon d'une fiche Trello (c'est ainsi que le
 * serveur les relit), puis le besoin en clair pour l'IA.
 */
const EXEMPLE_MESSAGE = `Nom : Laetitia Lacote
Email : laetitia.lacote@doltomonta.fr
Société : Centres sociaux Dolto et la Soie Montaberlet
Téléphone : 04 72 00 00 00
Effectif : 37 salariés
Objet : Demande de devis formation
Message :
Bonjour,

Nous sommes une association de centres sociaux de 37 salariés. Nous allons prochainement renouveler nos élections CSE et je souhaiterais être informée sur les formations obligatoires pour les membres du CSE.

Nous aurons 2 titulaires et 2 suppléants, soit 4 élus au total. Je voudrais un devis pour la formation SSCT initiale pour les 4 élus, ainsi que pour la formation Référent harcèlement (1 référent sera désigné).

Nous souhaitons que la formation se déroule dans nos locaux. Les dates restent à définir avec les élus.

Cordialement,
Laetitia Lacote`;

const VIDE = {
  prenom: '',
  nom: '',
  email: '',
  societeNom: '',
  numeroTelephone: '',
  effectifSociete: '',
  nbElus: '',
  objetMessage: '',
  message: '',
  mailPlanification: '',
  siteWeb: '',
};

// Champs de coordonnées, affichés en correction seulement : à la création,
// ils sont relus dans le message (voir l'en-tête du fichier). Plus rien
// d'obligatoire (migration 069) : le serveur exige seulement de quoi
// identifier la demande — une société, un nom, un email, ou un message.
const CHAMPS = [
  { cle: 'prenom', label: 'Prénom' },
  { cle: 'nom', label: 'Nom' },
  { cle: 'email', label: 'Email', type: 'email' },
  { cle: 'societeNom', label: 'Société', large: true },
  { cle: 'numeroTelephone', label: 'Téléphone' },
  { cle: 'effectifSociete', label: 'Effectif', aide: '37 salariés' },
  { cle: 'nbElus', label: 'Nombre d’élus', aide: '11' },
  { cle: 'objetMessage', label: 'Objet', large: true },
  { cle: 'siteWeb', label: 'Site web' },
];

/** Ce que la lecture des pièces verse dans le formulaire, champ par champ. */
const CHAMPS_DEPUIS_EXTRACTION = [
  ['prenom', (c) => c.contact_prenom],
  ['nom', (c) => c.contact_nom],
  ['email', (c) => c.email],
  ['societeNom', (c) => c.societe],
  ['numeroTelephone', (c) => c.telephone],
  ['effectifSociete', (c) => c.effectif],
];

/** Une phrase par formation lue, pour dire ce que la lecture a compris. */
function resumerExtraction(data) {
  const lignes = [];
  for (const f of data.formations || []) {
    const bouts = [f.intitule];
    if (f.code_catalogue) bouts.push(`(${f.code_catalogue})`);
    if (f.duree_jours) bouts.push(`· ${f.duree_jours} j`);
    if (f.nb_participants) bouts.push(`· ${f.nb_participants} pers.`);
    if (f.dates?.length) bouts.push(`· ${f.dates.join(', ')}`);
    lignes.push(bouts.join(' '));
  }
  const nb = (data.participants || []).length;
  if (nb) lignes.push(`${nb} participant(s) nommé(s)`);
  const f = data.formateur;
  if (f && (f.nom || f.prenom)) {
    lignes.push(`Formateur : ${[f.civilite, f.prenom, f.nom].filter(Boolean).join(' ')}`);
  }
  return lignes;
}

function NouvelleDemandePanel({ demande = null, onClose, onCree }) {
  const modification = Boolean(demande);

  const [form, setForm] = useState(() =>
    demande
      ? {
          ...VIDE,
          prenom: demande.prenom ?? '',
          nom: demande.nom ?? '',
          email: demande.email ?? '',
          societeNom: demande.societe_nom ?? '',
          numeroTelephone: demande.numero_telephone ?? '',
          effectifSociete: demande.effectif_societe ?? '',
          nbElus: demande.nb_elus ?? '',
          objetMessage: demande.objet_message ?? '',
          message: demande.message ?? '',
          mailPlanification: demande.mail_planification ?? demande.mailPlanification ?? '',
          siteWeb: demande.site_web ?? '',
        }
      : VIDE
  );
  const [saving, setSaving] = useState(false);
  const progressionEnregistrement = useProgression(saving, { estimeeMs: 6000 });
  const [erreur, setErreur] = useState(null);

  // Une demande saisie ici entre toujours « non traitée » : le devis est
  // rédigé automatiquement. Une affaire déjà entendue passe par la création
  // depuis un texte (pages Contrat, Convocation, Feuille), pas par ce formulaire.
  const statut = STATUT_GENERANT;

  /**
   * Pièces à joindre au dossier.
   *
   * Un dossier conclu ailleurs arrive avec ses documents : le devis signé, le
   * contrat, la feuille de présence scannée. Les saisir puis les déposer
   * depuis la fiche faisait deux gestes ; ils se joignent ici, à la demande
   * qu'ils accompagnent.
   *
   * Elles ne partent qu'APRÈS l'enregistrement : un fichier a besoin d'un
   * dossier où aller, et la demande doit exister pour cela.
   */
  const [pieces, setPieces] = useState([]);
  const [depotEnCours, setDepotEnCours] = useState(false);

  /**
   * Lecture des pièces par l'IA : ce qu'elle en a tiré (client, formations,
   * dates, participants) et le compte rendu fichier par fichier. La lecture
   * remplit les champs encore vides — jamais ceux déjà saisis — et part
   * avec la demande pour pré-remplir le dossier direct.
   */
  const [extraction, setExtraction] = useState(null);
  const [lectureEnCours, setLectureEnCours] = useState(false);
  const [lectureErreur, setLectureErreur] = useState(null);
  const progressionLecture = useProgression(lectureEnCours, { estimeeMs: 20000 });

  // La lecture en cours, pour que l'enregistrement l'attende : créer la
  // demande pendant qu'elle tourne perdrait ce qu'elle allait donner.
  const lectureRef = useRef(null);
  // La dernière lecture aboutie, lisible depuis `soumettre` même quand elle
  // s'achève pendant l'enregistrement (l'état React, lui, serait périmé).
  const extractionRef = useRef(null);

  function lirePieces(liste = pieces) {
    if (!liste.length) return Promise.resolve(null);
    const promesse = lirePiecesMaintenant(liste).finally(() => {
      if (lectureRef.current === promesse) lectureRef.current = null;
    });
    lectureRef.current = promesse;
    return promesse;
  }

  async function lirePiecesMaintenant(liste) {
    setLectureEnCours(true);
    setLectureErreur(null);
    try {
      const res = await extrairePiecesDemande(liste.map((p) => p.fichier));
      const data = res.data || {};
      const client = data.client || {};
      const lue = { data, pieces: res.pieces || [] };
      extractionRef.current = lue;
      setExtraction(lue);
      setForm((prev) => {
        const suite = { ...prev };
        for (const [cle, lire] of CHAMPS_DEPUIS_EXTRACTION) {
          const valeur = lire(client);
          if (!String(prev[cle] || '').trim() && valeur) suite[cle] = String(valeur);
        }
        if (!String(prev.message || '').trim() && data.resume) suite.message = data.resume;
        return suite;
      });
    } catch (err) {
      setLectureErreur(err.message || 'Lecture impossible.');
    } finally {
      setLectureEnCours(false);
    }
  }

  function ajouterPieces(fichiers) {
    const nouvelles = Array.from(fichiers || []).map((fichier) => ({
      fichier,
      categorie: 'contrat',
    }));
    if (!nouvelles.length) return;
    const liste = [...pieces, ...nouvelles];
    setPieces(liste);
    // Lues dès qu'elles arrivent : c'est d'elles que le dossier se remplit,
    // on n'a pas à le demander. Le bouton reste, pour relire après un retrait.
    if (!modification) lirePieces(liste);
  }

  /** Taille lisible, pour que l'utilisateur voie ce qu'il joint. */
  const taille = (octets) =>
    octets < 1024 * 1024
      ? `${Math.round(octets / 1024)} Ko`
      : `${(octets / (1024 * 1024)).toFixed(1)} Mo`;

  // Recherche d'un client du fichier (Clients) pour pré-remplir le contact :
  // la demande porte alors l'email qui la rattachera à son dossier.
  const [rechercheClient, setRechercheClient] = useState('');
  const [resultatsClients, setResultatsClients] = useState([]);
  const [rechercheEnCours, setRechercheEnCours] = useState(false);
  const [clientChoisi, setClientChoisi] = useState(null);

  useEffect(() => {
    const terme = rechercheClient.trim();
    if (terme.length < 2) {
      setResultatsClients([]);
      return undefined;
    }
    let annule = false;
    const timer = setTimeout(() => {
      setRechercheEnCours(true);
      getClients({ search: terme, limit: 8 })
        .then((res) => {
          if (!annule) setResultatsClients(res.data || []);
        })
        .catch(() => {
          if (!annule) setResultatsClients([]);
        })
        .finally(() => {
          if (!annule) setRechercheEnCours(false);
        });
    }, 300);
    return () => {
      annule = true;
      clearTimeout(timer);
    };
  }, [rechercheClient]);

  function choisirClient(client) {
    setClientChoisi(client);
    setRechercheClient('');
    setResultatsClients([]);
    setErreur(null);
    // On ne touche qu'aux champs que la fiche renseigne : le reste du
    // formulaire (message, effectif...) reste tel que saisi.
    setForm((prev) => ({
      ...prev,
      prenom: client.prenom || prev.prenom,
      nom: client.nom || prev.nom,
      email: client.email || prev.email,
      numeroTelephone: client.telephoneFixe || client.whatsapp || prev.numeroTelephone,
      societeNom: client.societeNom || prev.societeNom,
    }));
  }

  const maj = (cle) => (e) => setForm((prev) => ({ ...prev, [cle]: e.target.value }));

  async function soumettre(e) {
    e.preventDefault();
    setSaving(true);
    setErreur(null);

    try {
      // Une lecture encore en cours finit d'abord : ce qu'elle donne part
      // avec la demande, et décide de la pose du dossier.
      if (lectureRef.current) await lectureRef.current.catch(() => null);

      const res = modification
        ? await updateContact(demande.id, form)
        : await createContact({
            ...form,
            statut,
            extractionPieces: extractionRef.current?.data ?? null,
          });

      // Les pièces suivent, une fois le dossier ouvert. L'échec de l'une
      // n'annule pas la demande — elle est enregistrée, et le fichier se
      // redépose depuis sa fiche.
      const contactId = res.data?.id ?? demande?.id;
      const refuses = [];
      if (contactId && pieces.length) {
        setDepotEnCours(true);
        for (const piece of pieces) {
          try {
            await deposerDocumentContact(contactId, piece.fichier, {
              categorie: piece.categorie,
            });
          } catch (err) {
            refuses.push(`${piece.fichier.name} (${err.message})`);
          }
        }
        setDepotEnCours(false);
      }

      if (refuses.length) {
        setErreur(
          `Demande enregistrée, mais ${refuses.length} fichier(s) non déposé(s) : ` +
            `${refuses.join(', ')}. Reprenez-les depuis la fiche.`
        );
        setPieces([]);
        return;
      }

      onCree?.(res.data);
    } catch (err) {
      setErreur(err.message || 'Création impossible.');
    } finally {
      setSaving(false);
      setDepotEnCours(false);
    }
  }

  return (
    <div className="devis-modal-overlay" onClick={onClose}>
      <div className="devis-modal devis-modal-etroit" onClick={(e) => e.stopPropagation()}>
        <div className="devis-modal-header">
          <div>
            <div className="devis-modal-numero">
              {modification ? 'Modifier la demande' : 'Nouvelle demande'}
            </div>
            <div className="devis-modal-sub">
              {modification
                ? 'Correction possible tant qu’aucun devis n’a été généré.'
                : 'Saisie manuelle — les demandes réelles arrivent par l’import Trello.'}
            </div>
          </div>
          <div className="devis-modal-header-right">
            <button type="button" className="devis-drawer-close" onClick={onClose} aria-label="Fermer">
              ×
            </button>
          </div>
        </div>

        <div className="devis-modal-body">
          {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}


          <form onSubmit={soumettre}>
            {modification && (
              <RemplirParTexte
                contexte="demande de devis d'un client (coordonnées, société, effectif, objet, message)"
                champs={[
                  { cle: 'prenom', libelle: 'Prénom' },
                  { cle: 'nom', libelle: 'Nom' },
                  { cle: 'email', libelle: 'Email', type: 'email' },
                  { cle: 'societeNom', libelle: 'Société' },
                  { cle: 'numeroTelephone', libelle: 'Téléphone', type: 'telephone' },
                  { cle: 'effectifSociete', libelle: 'Effectif de la société' },
                  { cle: 'nbElus', libelle: "Nombre d'élus" },
                  { cle: 'objetMessage', libelle: 'Objet du message' },
                  { cle: 'message', libelle: 'Message' },
                  { cle: 'mailPlanification', libelle: 'Mail de planification' },
                  { cle: 'siteWeb', libelle: 'Site web' },
                ]}
                onValeurs={(v) => setForm((prev) => ({ ...prev, ...v }))}
              />
            )}
            {modification && (
            <>
            <div className="devis-form-section-title">Client existant</div>
            <div className="devis-client-recherche">
              <input
                type="text"
                value={rechercheClient}
                onChange={(e) => setRechercheClient(e.target.value)}
                placeholder="Rechercher dans le fichier clients : nom, email, société..."
                autoComplete="off"
              />
              {clientChoisi && (
                <div className="devis-client-choisi">
                  Contact repris de{' '}
                  <strong>
                    {[clientChoisi.prenom, clientChoisi.nom].filter(Boolean).join(' ') ||
                      clientChoisi.email}
                  </strong>
                  <button
                    type="button"
                    className="devis-btn devis-btn-sm"
                    onClick={() => setClientChoisi(null)}
                  >
                    Retirer
                  </button>
                </div>
              )}
              {(rechercheEnCours || resultatsClients.length > 0) && (
                <ul className="devis-client-resultats">
                  {rechercheEnCours && <li className="devis-client-resultat-etat">Recherche...</li>}
                  {resultatsClients.map((c) => (
                    <li key={c.id}>
                      <button type="button" onClick={() => choisirClient(c)}>
                        <span className="devis-client-resultat-nom">
                          {[c.prenom, c.nom].filter(Boolean).join(' ') || c.email}
                        </span>
                        <span className="devis-client-resultat-detail">
                          {[c.email, c.societeNom, c.fonction].filter(Boolean).join(' · ')}
                        </span>
                      </button>
                    </li>
                  ))}
                  {!rechercheEnCours && rechercheClient.trim().length >= 2 && resultatsClients.length === 0 && (
                    <li className="devis-client-resultat-etat">Aucun client trouvé.</li>
                  )}
                </ul>
              )}
            </div>

            <div className="devis-form-section-title">Contact</div>
            <div className="devis-form-row">
              {CHAMPS.map((c) => (
                <label
                  key={c.cle}
                  className="devis-form-field"
                  style={c.large ? { flex: 2, minWidth: 220 } : undefined}
                >
                  <span>
                    {c.label}
                    {c.requis ? ' *' : ''}
                  </span>
                  <input
                    type={c.type || 'text'}
                    value={form[c.cle]}
                    onChange={maj(c.cle)}
                    placeholder={c.aide || ''}
                    required={c.requis}
                  />
                </label>
              ))}
            </div>
            </>
            )}

            <div className="devis-form-section-title">Message de la demande</div>
            <label className="devis-form-field">
              <span>
                Message — c&apos;est ce texte que l&apos;IA analyse pour construire le devis
              </span>
              <textarea
                rows={modification ? 12 : 16}
                value={form.message}
                onChange={maj('message')}
                placeholder={
                  modification
                    ? 'Effectif, composition du CSE, formations souhaitées, format, période...'
                    : EXEMPLE_MESSAGE
                }
                required={!modification}
              />
            </label>
            {!modification && (
              <p className="param-hint">
                Les coordonnées sont lues dans le message ; l’IA complète ce qui manque.
              </p>
            )}

            <div className="devis-form-section-title">Pièces du dossier</div>
            <p className="param-hint">
              Choisissez la nature de chaque pièce ; dépôt à l’enregistrement.
              {!modification && (
                <>
                  {' '}
                  « Lire les pièces » remplit les champs vides (PDF texte ou Word, pas de scan).
                </>
              )}
            </p>

            <label className="devis-form-field">
              <span>Ajouter des fichiers</span>
              <input
                type="file"
                multiple
                accept={FORMATS_ACCEPTES}
                onChange={(e) => {
                  ajouterPieces(e.target.files);
                  // Vidé pour que rechoisir le même fichier redéclenche le
                  // changement : sans cela, un retrait suivi d'un rajout
                  // resterait sans effet.
                  e.target.value = '';
                }}
                disabled={saving}
              />
            </label>

            {pieces.map((piece, i) => (
              <div className="devis-form-row" key={`${piece.fichier.name}-${i}`}>
                <label className="devis-form-field" style={{ flex: 2 }}>
                  <span>Nature</span>
                  <SelectRecherche
                    value={piece.categorie}
                    onChange={(e) =>
                      setPieces((p) =>
                        p.map((x, k) => (k === i ? { ...x, categorie: e.target.value } : x))
                      )
                    }
                    disabled={saving}
                  >
                    {CATEGORIES_DOCUMENT.map((c) => (
                      <option key={c.valeur} value={c.valeur}>
                        {c.label}
                      </option>
                    ))}
                  </SelectRecherche>
                </label>
                <label className="devis-form-field" style={{ flex: 3 }}>
                  <span>Fichier</span>
                  <div className="devis-form-inline">
                    <span className="devis-status-detail" title={piece.fichier.name}>
                      {piece.fichier.name} · {taille(piece.fichier.size)}
                    </span>
                    <button
                      type="button"
                      className="devis-btn devis-btn-sm devis-btn-danger"
                      onClick={() => setPieces((p) => p.filter((_, k) => k !== i))}
                      disabled={saving}
                    >
                      Retirer
                    </button>
                  </div>
                </label>
              </div>
            ))}

            {!modification && pieces.length > 0 && (
              <div className="devis-form-field">
                <BarreProgression valeur={progressionLecture} libelle="Lecture des pièces par l’IA" estimation />
                <div className="devis-form-inline">
                  <button
                    type="button"
                    className="devis-btn devis-btn-sm devis-btn-primary"
                    onClick={lirePieces}
                    disabled={saving || lectureEnCours}
                    title="Lit les fichiers ci-dessus et remplit les champs encore vides"
                  >
                    {lectureEnCours ? 'Lecture des pièces…' : 'Lire les pièces'}
                  </button>
                  {extraction && !lectureEnCours && (
                    <span className="devis-status-detail">
                      {extraction.pieces.filter((p) => p.lu).length} pièce(s) lue(s)
                      {extraction.pieces.some((p) => !p.lu) &&
                        ` — non lue(s) : ${extraction.pieces
                          .filter((p) => !p.lu)
                          .map((p) => `${p.nom} (${p.raison})`)
                          .join(', ')}`}
                    </span>
                  )}
                </div>
                {lectureErreur && (
                  <div className="devis-alert devis-alert-erreur">{lectureErreur}</div>
                )}
                {extraction && !lectureEnCours && (
                  <div className="devis-alert devis-alert-attention">
                    <strong>Ce que la lecture a compris</strong> — à relire.
                    {resumerExtraction(extraction.data).length ? (
                      <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                        {resumerExtraction(extraction.data).map((l, i) => (
                          <li key={i}>{l}</li>
                        ))}
                      </ul>
                    ) : (
                      <div>Aucune formation ni participant reconnu dans ces pièces.</div>
                    )}
                  </div>
                )}
              </div>
            )}

            <BarreProgression valeur={progressionEnregistrement} libelle="Enregistrement de la demande" estimation />
            <div className="devis-modal-actions">
              <button type="button" className="devis-btn" onClick={onClose} disabled={saving}>
                Annuler
              </button>
              <button type="submit" className="devis-btn devis-btn-primary" disabled={saving}>
                {depotEnCours
                  ? 'Dépôt des pièces…'
                  : saving
                    ? modification
                      ? 'Enregistrement'
                      : 'Création'
                    : modification
                      ? 'Enregistrer'
                      : 'Créer la demande'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default NouvelleDemandePanel;
